import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { post, get, put, del } from "../../services/api.js";

// Get API base URL for constructing file URLs
const getApiBaseUrl = () => {
  return (
    process.env.REACT_APP_API_URL_LOGIN_AND_LOGOUT
  );
};

/**
 * Construct file URL from file_path with defensive sanitization
 * @param {string} file_path - File path to construct URL from
 * @returns {string|null} Constructed URL or null if invalid
 */
const constructFileUrl = (file_path) => {
  // Defensive: validate input
  if (!file_path || typeof file_path !== "string") {
    return null;
  }

  // If file_path is already a full URL (starts with http:// or https://), return as is
  if (file_path.startsWith("http://") || file_path.startsWith("https://")) {
    return file_path;
  }

  // If file_path is a blob URL, return as is
  if (file_path.startsWith("blob:")) {
    return file_path;
  }

  // Defensive: sanitize file path to prevent directory traversal
  // Remove dangerous path components
  let sanitizedPath = file_path
    .replace(/\.\./g, "") // Remove .. sequences
    .replace(/[<>:"|?*\x00-\x1f]/g, "") // Remove invalid characters
    .trim();

  // Remove leading slashes to prevent absolute paths
  sanitizedPath = sanitizedPath.replace(/^\/+/, "");

  // If sanitization resulted in empty path, return null
  if (!sanitizedPath) {
    return null;
  }

  // Otherwise, construct full URL from API base URL
  const apiBaseUrl = getApiBaseUrl();
  return `${apiBaseUrl}/${sanitizedPath}`;
};

// Normalize API response to match component expectations
const normalizeDirectoryItem = (item) => {
  if (!item) return item;

  const file_path = item.file_path || item.filePath;
  const thumbnail_path = item.thumbnail_path || item.thumbnailPath;

  const normalized = {
    ...item,
    // Map updated_at to dateModified for component compatibility
    dateModified: item.updated_at || item.dateModified || item.created_at,
    // Map file_type to fileType if needed
    fileType: item.file_type || item.fileType,
    // Map mime_type to mimeType if needed
    mimeType: item.mime_type || item.mimeType,
    // Map file_path to filePath and keep both for compatibility
    filePath: file_path,
    // Keep original file_path for backward compatibility
    file_path: file_path,
    // Map thumbnail_path to thumbnailPath and keep both for compatibility
    thumbnailPath: thumbnail_path,
    thumbnail_path: thumbnail_path,
    // Construct fileUrl from file_path for preview components
    fileUrl: item.fileUrl || (file_path ? constructFileUrl(file_path) : null),
    // Construct thumbnailUrl from thumbnail_path for thumbnail previews
    thumbnailUrl:
      item.thumbnailUrl ||
      (thumbnail_path ? constructFileUrl(thumbnail_path) : null),
    // Ensure parent_id is consistent (keep both for compatibility)
    parentId: item.parent_id || item.parentId,
    // Map owner if not present (default to 'Me')
    owner: item.owner || "Me",
  };

  // Recursively normalize children
  if (item.children && Array.isArray(item.children)) {
    normalized.children = item.children.map((child) =>
      normalizeDirectoryItem(child)
    );
  }

  return normalized;
};

/**
 * Helper function to find a folder by ID in the tree
 * @param {Array} items - The tree of items to search
 * @param {string|number} folderId - The ID of the folder to find
 * @returns {Object|null} The found folder or null
 */
const findFolderById = (items, folderId) => {
  if (!items || !Array.isArray(items) || !folderId) {
    return null;
  }

  for (const item of items) {
    const itemId = String(item.id);
    const targetId = String(folderId);

    if (itemId === targetId) {
      return item;
    }

    if (item.children && Array.isArray(item.children)) {
      const found = findFolderById(item.children, folderId);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

/**
 * Sync currentFolder and breadcrumbs with the updated tree
 * This ensures that when the tree is updated (deletion, addition, etc.),
 * the currentFolder and breadcrumbs reference the fresh objects from the tree
 * @param {Object} state - The Redux state
 */
const syncCurrentFolderAndBreadcrumbs = (state) => {
  // Sync currentFolder if it exists
  if (state.currentFolder && state.currentFolder.id) {
    const freshCurrentFolder = findFolderById(
      state.items,
      state.currentFolder.id
    );
    if (freshCurrentFolder) {
      state.currentFolder = freshCurrentFolder;
    } else {
      // If currentFolder was deleted, reset to root
      state.currentFolder = null;
      state.breadcrumbs = [];
    }
  }

  // Sync breadcrumbs with fresh references
  if (state.breadcrumbs && state.breadcrumbs.length > 0) {
    const originalBreadcrumbsLength = state.breadcrumbs.length;
    const freshBreadcrumbs = [];
    for (const breadcrumb of state.breadcrumbs) {
      if (breadcrumb && breadcrumb.id) {
        const freshBreadcrumb = findFolderById(state.items, breadcrumb.id);
        if (freshBreadcrumb) {
          freshBreadcrumbs.push(freshBreadcrumb);
        } else {
          // If a breadcrumb folder was deleted, stop here
          break;
        }
      }
    }
    state.breadcrumbs = freshBreadcrumbs;

    // If breadcrumbs were truncated, update currentFolder to the last valid breadcrumb
    if (
      freshBreadcrumbs.length > 0 &&
      freshBreadcrumbs.length < originalBreadcrumbsLength
    ) {
      state.currentFolder = freshBreadcrumbs[freshBreadcrumbs.length - 1];
    }
  }
};

// Async thunk for fetching all directories via GET
export const fetchDirectories = createAsyncThunk(
  "files/fetchDirectories",
  async (params = {}, { rejectWithValue }) => {
    try {
      // Handle both object and no-argument calls
      const starred = params?.starred || false;
      const response = await get("/directory", { starred: starred });
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch directories");
    }
  }
);

export const createFolderThunk = createAsyncThunk(
  "files/createFolder",
  async (
    {
      name,
      parent_id,
      type = "folder",
      owner = "Me",
      owner_id = null,
      dateModified = new Date().toLocaleDateString(),
      children = [],
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post("/directory", {
        name,
        parent_id: parent_id || null,
        type,
        owner,
        owner_id: owner_id || null,
        dateModified,
        children: children || [],
      });
      console.log("createFolderThunk response", response);

      // Check if response indicates failure (double-check even though api.js should handle this)
      if (response && response.success === false) {
        const errorMessage =
          response.message || response.error || "Failed to create folder";
        return rejectWithValue(errorMessage);
      }

      // Include the parentId from request in response for proper state update
      return {
        ...response,
        parent_id: parent_id || null, // Ensure parentId is included
      };
    } catch (error) {
      // Extract error message from error object
      const errorMessage =
        error.message ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create folder";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for updating/renaming a directory or file
export const updateDirectoryThunk = createAsyncThunk(
  "files/updateDirectory",
  async ({ id, name, ...otherUpdates }, { rejectWithValue }) => {
    try {
      const response = await put(`/directory/${id}`, {
        name,
        id,
        ...otherUpdates,
      });
      console.log("updateDirectoryThunk response", response);
      return { id, ...response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update directory");
    }
  }
);

// Async thunk for deleting a directory or file
export const deleteDirectoryThunk = createAsyncThunk(
  "files/deleteDirectory",
  async (id, { rejectWithValue }) => {
    try {
      const response = await del(`/directory/${id}`);
      console.log("deleteDirectoryThunk response", response);
      return id; // Return the deleted ID for state update
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete directory");
    }
  }
);

// Async thunk for toggling star/favorite status
export const toggleStarThunk = createAsyncThunk(
  "files/toggleStar",
  async ({ id, starred }, { rejectWithValue }) => {
    try {
      // Validate id before making the request
      if (!id || id === "undefined" || id === undefined) {
        console.error("toggleStarThunk: id is missing or invalid", {
          id,
          starred,
        });
        return rejectWithValue("File ID is required to toggle star status");
      }

      const response = await put(`/directory/${id}`, {
        id,
        starred: !starred,
      });
      console.log("toggleStarThunk response", response);
      return { id, starred: !starred, ...response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to toggle star status");
    }
  }
);
export const toggleStarBulkThunk = createAsyncThunk(
  "files/toggleStarBulkThunk",
  async ({ ids, starred }, { rejectWithValue }) => {
    try {
      const response = await put(`/directory/bulkToggleStar`, {
        ids,
        starred,
      });
      console.log("toggleStarBulkThunk response", response);
      return { ids, starred, ...response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to toggle star status");
    }
  }
);
export const addDrawingThunk = createAsyncThunk(
  "files/addDrawing",
  async (
    {
      name,
      file,
      type = "file",
      file_type,
      mime_type,
      size,
      owner,
      owner_id = null,
      dateModified,
      children = [],
      parent_id = null,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post("/directory", {
        name,
        file_path: file, // Backend expects file_path as base64 data URL
        parent_id: parent_id || null,
        type,
        file_type: file_type || null,
        mime_type: mime_type || null,
        size: size || null,
        owner: owner || "Me",
        owner_id: owner_id || null,
        dateModified: dateModified || new Date().toLocaleDateString(),
        children: children || [],
      });
      if (response && response.success === false) {
        const errorMessage =
          response.message || response.error || "Failed to add drawing";
        return rejectWithValue(errorMessage);
      }
      return {
        ...response,
        parent_id: parent_id || null, // Ensure parentId is included
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to add drawing");
    }
  }
);
const initialState = {
  items: [],
  selectedIds: [],
  sortField: "name",
  sortDirection: "asc",
  searchQuery: "",
  currentFolder: null,
  breadcrumbs: [],
  previewFile: null,
  expandedFolders: [],
  selectedDrawingForView: null,
  viewMode: "list",
  activeFilters: {
    type: "all",
    owner: "all",
    dateRange: "all",
  },
  // Dialog states
  showAddFolderDialog: false,
  showAddDrawingDialog: false,
  showInfoPanel: false,
  newFolderName: "",
  newDrawingName: "",
  targetFolder: null,
  infoFile: null,
  selectedFile: null,
  // Loading and error states
  isCreatingFolder: false,
  createFolderError: null,
  isLoadingDirectories: false,
  fetchDirectoriesError: null,
  isUpdatingDirectory: false,
  updateDirectoryError: null,
  isDeletingDirectory: false,
  deleteDirectoryError: null,
};

const filesSlice = createSlice({
  name: "files",
  initialState,
  reducers: {
    setFiles: (state, action) => {
      state.items = action.payload;
    },
    addFile: (state, action) => {
      state.items.push(action.payload);
    },
    updateFile: (state, action) => {
      const { id, updates } = action.payload;
      const updateFileRecursive = (files) => {
        return files.map((file) => {
          if (file.id === id) {
            return { ...file, ...updates };
          }
          if (file.children) {
            return {
              ...file,
              children: updateFileRecursive(file.children),
            };
          }
          return file;
        });
      };
      state.items = updateFileRecursive(state.items);
    },
    deleteFile: (state, action) => {
      const deleteFileRecursive = (files) => {
        return files.filter((file) => {
          if (file.id === action.payload) {
            return false;
          }
          if (file.children) {
            file.children = deleteFileRecursive(file.children);
          }
          return true;
        });
      };
      state.items = deleteFileRecursive(state.items);
      // Remove from selected if it was selected
      state.selectedIds = state.selectedIds.filter(
        (id) => id !== action.payload
      );
    },
    setSelectedIds: (state, action) => {
      console.log("setSelectedIds action.payload", action.payload);
      state.selectedIds = action.payload;
    },
    toggleSelectedId: (state, action) => {
      const id = action.payload;
      const index = state.selectedIds.indexOf(id);
      if (index > -1) {
        state.selectedIds.splice(index, 1);
      } else {
        state.selectedIds.push(id);
      }
    },
    setSortField: (state, action) => {
      if (state.sortField === action.payload) {
        state.sortDirection = state.sortDirection === "asc" ? "desc" : "asc";
      } else {
        state.sortField = action.payload;
        state.sortDirection = "asc";
      }
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setCurrentFolder: (state, action) => {
      state.currentFolder = action.payload;
    },
    addBreadcrumb: (state, action) => {
      state.breadcrumbs.push(action.payload);
    },
    setBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload;
    },
    setPreviewFile: (state, action) => {
      state.previewFile = action.payload;
    },
    toggleExpandedFolder: (state, action) => {
      const folderId = action.payload;
      const index = state.expandedFolders.indexOf(folderId);
      if (index > -1) {
        state.expandedFolders.splice(index, 1);
      } else {
        state.expandedFolders.push(folderId);
      }
    },
    setExpandedFolders: (state, action) => {
      state.expandedFolders = action.payload;
    },
    setSelectedDrawingForView: (state, action) => {
      state.selectedDrawingForView = action.payload;
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    setActiveFilters: (state, action) => {
      state.activeFilters = { ...state.activeFilters, ...action.payload };
    },
    clearFilters: (state) => {
      state.activeFilters = {
        type: "all",
        owner: "all",
        dateRange: "all",
      };
    },
    // Dialog actions
    setShowAddFolderDialog: (state, action) => {
      state.showAddFolderDialog = action.payload;
    },
    setShowAddDrawingDialog: (state, action) => {
      state.showAddDrawingDialog = action.payload;
    },
    setShowInfoPanel: (state, action) => {
      state.showInfoPanel = action.payload;
    },
    setNewFolderName: (state, action) => {
      state.newFolderName = action.payload;
    },
    setNewDrawingName: (state, action) => {
      state.newDrawingName = action.payload;
    },
    setTargetFolder: (state, action) => {
      state.targetFolder = action.payload;
    },
    setInfoFile: (state, action) => {
      state.infoFile = action.payload;
    },
    setSelectedFile: (state, action) => {
      state.selectedFile = action.payload;
    },
    // Complex actions
    createFolderAction: (state, action) => {
      const { name, parent_id } = action.payload;
      // console.log('createFolderAction', name, parentId);
      const newFolder = {
        id: `folder-${Date.now()}`,
        name,
        type: "folder",
        children: [],
        dateModified: new Date().toLocaleDateString(),
        owner: "Me",
      };

      if (parent_id) {
        const addToFolder = (files) => {
          return files.map((file) => {
            if (file.id === parent_id) {
              return {
                ...file,
                children: [...(file.children || []), newFolder],
              };
            }
            if (file.children) {
              return {
                ...file,
                children: addToFolder(file.children),
              };
            }
            return file;
          });
        };
        state.items = addToFolder(state.items);
        // Auto-expand parent folder
        if (!state.expandedFolders.includes(parent_id)) {
          state.expandedFolders.push(parent_id);
        }
      } else {
        state.items.push(newFolder);
      }
    },
    createDrawing: (state, action) => {
      const { name, file, parentId } = action.payload;
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "pdf";
      const fileName = name.includes(".") ? name : `${name}.${fileExtension}`;
      let fileType = file.type || "application/pdf";
      if (fileExtension === "dwg" || fileExtension === "dxf") {
        fileType = "DWG";
      }
      const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      const fileSize = `${fileSizeInMB} MB`;
      const fileUrl = URL.createObjectURL(file);

      const newDrawing = {
        id: `file-${Date.now()}`,
        name: fileName,
        type: "file",
        fileType: fileType,
        size: fileSize,
        dateModified: new Date().toLocaleDateString(),
        owner: "Me",
        fileUrl: fileUrl,
      };

      if (parentId) {
        const addToFolder = (files) => {
          return files.map((file) => {
            if (file.id === parentId) {
              return {
                ...file,
                children: [...(file.children || []), newDrawing],
              };
            }
            if (file.children) {
              return {
                ...file,
                children: addToFolder(file.children),
              };
            }
            return file;
          });
        };
        state.items = addToFolder(state.items);
        // Auto-expand parent folder
        if (!state.expandedFolders.includes(parentId)) {
          state.expandedFolders.push(parentId);
        }
      } else {
        state.items.push(newDrawing);
      }
    },
    toggleStar: (state, action) => {
      const fileId = action.payload;

      // Helper function to recursively update star status for all children
      const updateChildrenStarStatus = (children, newStarredStatus) => {
        return children.map((child) => {
          const updatedChild = { ...child, starred: newStarredStatus };
          if (child.children && child.children.length > 0) {
            updatedChild.children = updateChildrenStarStatus(
              child.children,
              newStarredStatus
            );
          }
          return updatedChild;
        });
      };

      const toggleStarRecursive = (files) => {
        return files.map((file) => {
          if (String(file.id) === String(fileId)) {
            const newStarredStatus = !file.starred;
            const updatedFile = { ...file, starred: newStarredStatus };

            // If it's a folder, recursively update all children
            if (file.type === "folder" && file.children) {
              updatedFile.children = updateChildrenStarStatus(
                file.children,
                newStarredStatus
              );
            }

            return updatedFile;
          }
          if (file.children) {
            return {
              ...file,
              children: toggleStarRecursive(file.children),
            };
          }
          return file;
        });
      };
      state.items = toggleStarRecursive(state.items);
    },
    // Clear error states
    clearCreateFolderError: (state) => {
      state.createFolderError = null;
    },
    clearUpdateDirectoryError: (state) => {
      state.updateDirectoryError = null;
    },
    clearDeleteDirectoryError: (state) => {
      state.deleteDirectoryError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchDirectories pending
      .addCase(fetchDirectories.pending, (state) => {
        state.isLoadingDirectories = true;
        state.fetchDirectoriesError = null;
      })
      // Handle fetchDirectories fulfilled
      .addCase(fetchDirectories.fulfilled, (state, action) => {
        state.isLoadingDirectories = false;
        state.fetchDirectoriesError = null;

        // Set the items from API response
        // API response structure: { success: true, data: { directory: [...], pagination: {...} } }
        let items = [];
        if (
          action.payload &&
          action.payload.data &&
          action.payload.data.directory
        ) {
          // Handle nested structure: payload.data.directory
          items = Array.isArray(action.payload.data.directory)
            ? action.payload.data.directory
            : [];
        } else if (Array.isArray(action.payload)) {
          // Handle direct array response
          items = action.payload;
        } else if (action.payload && action.payload.directory) {
          // Handle payload.directory
          items = Array.isArray(action.payload.directory)
            ? action.payload.directory
            : [];
        } else if (action.payload && action.payload.directories) {
          // Handle payload.directories
          items = Array.isArray(action.payload.directories)
            ? action.payload.directories
            : [];
        } else if (
          action.payload &&
          action.payload.data &&
          Array.isArray(action.payload.data)
        ) {
          // Handle payload.data as array
          items = action.payload.data;
        } else if (action.payload) {
          // If response is a single object, wrap it in an array
          items = [action.payload];
        }

        // Normalize all items to match component expectations
        state.items = items.map((item) => normalizeDirectoryItem(item));
        // Sync currentFolder and breadcrumbs with updated tree
        syncCurrentFolderAndBreadcrumbs(state);
      })
      // Handle fetchDirectories rejected
      .addCase(fetchDirectories.rejected, (state, action) => {
        state.isLoadingDirectories = false;
        state.fetchDirectoriesError =
          action.payload || "Failed to fetch directories";
      })
      // Handle createFolderThunk pending
      .addCase(createFolderThunk.pending, (state) => {
        state.isCreatingFolder = true;
        state.createFolderError = null;
      })
      // Handle createFolderThunk fulfilled
      .addCase(createFolderThunk.fulfilled, (state, action) => {
        state.isCreatingFolder = false;
        state.createFolderError = null;

        // Extract folder data from API response
        // API should return: { id, name, type: 'folder', parentId, ... }
        const folderData = action.payload;
        const newFolder = {
          id: folderData.id || `folder-${Date.now()}`,
          name: folderData.name || "Untitled Folder",
          type: folderData.type || "folder",
          children: folderData.children || [],
          dateModified:
            folderData.dateModified || new Date().toLocaleDateString(),
          owner: folderData.owner || "Me",
          ...folderData, // Include any additional fields from API
        };

        const parentId = folderData.parentId || null;

        if (parentId) {
          const addToFolder = (files) => {
            return files.map((file) => {
              if (file.id === parentId) {
                return {
                  ...file,
                  children: [...(file.children || []), newFolder],
                };
              }
              if (file.children) {
                return {
                  ...file,
                  children: addToFolder(file.children),
                };
              }
              return file;
            });
          };
          state.items = addToFolder(state.items);
          // Auto-expand parent folder
          if (!state.expandedFolders.includes(parentId)) {
            state.expandedFolders.push(parentId);
          }
        } else {
          state.items.push(newFolder);
        }
        // Sync currentFolder and breadcrumbs with updated tree
        syncCurrentFolderAndBreadcrumbs(state);
      })
      // Handle createFolderThunk rejected
      .addCase(createFolderThunk.rejected, (state, action) => {
        state.isCreatingFolder = false;
        state.createFolderError = action.payload || "Failed to create folder";
      })
      // Handle updateDirectoryThunk pending
      .addCase(updateDirectoryThunk.pending, (state) => {
        state.isUpdatingDirectory = true;
        state.updateDirectoryError = null;
      })
      // Handle updateDirectoryThunk fulfilled
      .addCase(updateDirectoryThunk.fulfilled, (state, action) => {
        state.isUpdatingDirectory = false;
        state.updateDirectoryError = null;
        const { id, ...updates } = action.payload;
        const updateFileRecursive = (files) => {
          return files.map((file) => {
            const fileId = String(file.id);
            const targetId = String(id);
            if (fileId === targetId) {
              return { ...file, ...updates };
            }
            if (file.children && Array.isArray(file.children)) {
              return {
                ...file,
                children: updateFileRecursive(file.children),
              };
            }
            return file;
          });
        };
        state.items = updateFileRecursive(state.items);
        // Sync currentFolder and breadcrumbs with updated tree
        syncCurrentFolderAndBreadcrumbs(state);
      })
      // Handle updateDirectoryThunk rejected
      .addCase(updateDirectoryThunk.rejected, (state, action) => {
        state.isUpdatingDirectory = false;
        state.updateDirectoryError =
          action.payload || "Failed to update directory";
      })
      // Handle deleteDirectoryThunk pending
      .addCase(deleteDirectoryThunk.pending, (state) => {
        state.isDeletingDirectory = true;
        state.deleteDirectoryError = null;
      })
      // Handle deleteDirectoryThunk fulfilled
      .addCase(deleteDirectoryThunk.fulfilled, (state, action) => {
        state.isDeletingDirectory = false;
        state.deleteDirectoryError = null;
        const deletedId = action.payload;
        const deleteFileRecursive = (files) => {
          return files.filter((file) => {
            const fileId = String(file.id);
            const targetId = String(deletedId);
            if (fileId === targetId) {
              return false;
            }
            if (file.children && Array.isArray(file.children)) {
              file.children = deleteFileRecursive(file.children);
            }
            return true;
          });
        };
        state.items = deleteFileRecursive(state.items);
        // Remove from selected if it was selected
        state.selectedIds = state.selectedIds.filter(
          (id) => String(id) !== String(deletedId)
        );
        // Sync currentFolder and breadcrumbs with updated tree
        syncCurrentFolderAndBreadcrumbs(state);
      })
      // Handle deleteDirectoryThunk rejected
      .addCase(deleteDirectoryThunk.rejected, (state, action) => {
        state.isDeletingDirectory = false;
        state.deleteDirectoryError =
          action.payload || "Failed to delete directory";
      })
      .addCase(addDrawingThunk.pending, (state) => {
        state.isCreatingFolder = true; // Reuse loading state
        state.createFolderError = null;
      })
      .addCase(addDrawingThunk.fulfilled, (state, action) => {
        state.isCreatingFolder = false;
        state.createFolderError = null;

        // Extract file data from API response
        const fileData = action.payload;
        // Use normalizeDirectoryItem to ensure consistent URL construction
        const normalizedData = normalizeDirectoryItem(fileData);
        const newFile = {
          id: normalizedData.id || fileData.id || `file-${Date.now()}`,
          name: normalizedData.name || fileData.name || "Untitled File",
          type: normalizedData.type || fileData.type || "file",
          fileType:
            normalizedData.fileType || fileData.file_type || fileData.fileType,
          size: normalizedData.size || fileData.size || "0 MB",
          dateModified:
            normalizedData.dateModified ||
            fileData.updated_at ||
            fileData.dateModified ||
            fileData.created_at ||
            new Date().toLocaleDateString(),
          owner: normalizedData.owner || fileData.owner || "Me",
          fileUrl: normalizedData.fileUrl, // Use normalized fileUrl
          file_path: normalizedData.file_path || fileData.file_path,
          ...normalizedData, // Include all normalized fields
        };

        const parentId = fileData.parent_id || fileData.parentId || null;

        if (parentId) {
          const addToFolder = (files) => {
            return files.map((file) => {
              const fileId = String(file.id);
              const targetId = String(parentId);
              if (fileId === targetId) {
                return {
                  ...file,
                  children: [...(file.children || []), newFile],
                };
              }
              if (file.children) {
                return {
                  ...file,
                  children: addToFolder(file.children),
                };
              }
              return file;
            });
          };
          state.items = addToFolder(state.items);
          // Auto-expand parent folder
          if (!state.expandedFolders.includes(parentId)) {
            state.expandedFolders.push(parentId);
          }
        } else {
          state.items.push(newFile);
        }
        // Sync currentFolder and breadcrumbs with updated tree
        syncCurrentFolderAndBreadcrumbs(state);
      })
      .addCase(addDrawingThunk.rejected, (state, action) => {
        state.isCreatingFolder = false;
        state.createFolderError = action.payload || "Failed to upload file";
      })
      // Handle toggleStarThunk fulfilled
      .addCase(toggleStarThunk.fulfilled, (state, action) => {
        const { id, starred } = action.payload;

        // Helper function to recursively update star status
        const updateStarRecursive = (files) => {
          return files.map((file) => {
            const fileId = String(file.id);
            const targetId = String(id);

            // If this is the target file/folder, update it and all its children
            if (fileId === targetId) {
              const updatedFile = { ...file, starred };

              // If it's a folder, recursively update all children
              if (file.type === "folder" && file.children) {
                updatedFile.children = updateChildrenStarStatus(
                  file.children,
                  starred
                );
              }

              return updatedFile;
            }

            // Recursively check children
            if (file.children) {
              return {
                ...file,
                children: updateStarRecursive(file.children),
              };
            }

            return file;
          });
        };

        // Helper function to update star status for all children recursively
        const updateChildrenStarStatus = (children, newStarredStatus) => {
          return children.map((child) => {
            const updatedChild = { ...child, starred: newStarredStatus };
            if (child.children && child.children.length > 0) {
              updatedChild.children = updateChildrenStarStatus(
                child.children,
                newStarredStatus
              );
            }
            return updatedChild;
          });
        };

        state.items = updateStarRecursive(state.items);
        // Sync currentFolder and breadcrumbs with updated tree
        syncCurrentFolderAndBreadcrumbs(state);
      })
      // Handle toggleStarThunk rejected
      .addCase(toggleStarThunk.rejected, (state, action) => {
        // Error handling is done in component
        console.error("Failed to toggle star:", action.payload);
      })
      // Handle toggleStarBulkThunk fulfilled
      .addCase(toggleStarBulkThunk.fulfilled, (state, action) => {
        const { ids, starred } = action.payload;
        const updateStarRecursive = (files) => {
          return files.map((file) => {
            const fileId = String(file.id);
            if (ids.includes(fileId)) {
              console.log("fileId", fileId);
              const updatedFile = { ...file, starred: starred };
              return updatedFile;
            }
            if (file.children) {
              return {
                ...file,
                children: updateStarRecursive(file.children),
              };
            }
            return file;
          });
        };
        state.items = updateStarRecursive(state.items);
        // Sync currentFolder and breadcrumbs with updated tree
        syncCurrentFolderAndBreadcrumbs(state);
      })
      .addCase(toggleStarBulkThunk.rejected, (state, action) => {
        state.isUpdatingDirectory = false;
        state.updateDirectoryError =
          action.payload || "Failed to toggle star bulk";
      })
      .addCase(toggleStarBulkThunk.pending, (state) => {
        state.isUpdatingDirectory = true;
        state.updateDirectoryError = null;
      });
  },
});

export const {
  setFiles,
  addFile,
  updateFile,
  deleteFile,
  setSelectedIds,
  toggleSelectedId,
  setSortField,
  setSearchQuery,
  setCurrentFolder,
  addBreadcrumb,
  setBreadcrumbs,
  setPreviewFile,
  toggleExpandedFolder,
  setExpandedFolders,
  setSelectedDrawingForView,
  setViewMode,
  setActiveFilters,
  clearFilters,
  setShowAddFolderDialog,
  setShowAddDrawingDialog,
  setShowInfoPanel,
  setNewFolderName,
  setNewDrawingName,
  setTargetFolder,
  setInfoFile,
  setSelectedFile,
  createDrawing,
  toggleStar,
  clearCreateFolderError,
  clearUpdateDirectoryError,
  clearDeleteDirectoryError,
} = filesSlice.actions;

export default filesSlice.reducer;
